import { DataProxy } from 'apollo-cache'
import { WritableDraft } from 'immer/dist/internal'

import {
  AnnotationArtboardInfoFragment,
  AnnotationArtboardInfoFragmentDoc,
  AnnotationSubjectFragment,
  BaseAnnotationFragment,
  GetAnnotationsQuery,
} from '@sketch/gql-types'

import {
  getAnnotationSubject,
  getBaseAnnotation,
  updateAnnotationsAllOrder,
  updateAnnotationsDotsQuery,
  updateAnnotationsInboxOrder,
  updateAnnotationsQuery,
  updateMultipleAnnotationDotQueries,
  updateMultipleAnnotationQueries,
  isSameAnnotationSubject,
  getCacheInputFromAnnotationSubject,
} from './cache'

import { errorPreventiveFragmentRead, removeFromPaginated } from 'utils'
import { dataIdFromObject } from '@sketch/graphql-cache'

interface AdditionalProps {
  shareIdentifier: string
  versionIdentifier?: string | null
}

export const createNewAnnotationInCache = (
  apollo: DataProxy,
  { shareIdentifier, versionIdentifier }: AdditionalProps,
  annotation: BaseAnnotationFragment
) => {
  /**
   * In order to successfully add an annotation to the list
   * we need to:
   * [programmatically]
   * - add the annotation to its annotations listing [1]
   * - add to the annotation dots query [2]
   *
   * [fragment BaseAnnotation refetch]
   * - update the subject annotation counters
   */

  const subject = getCacheInputFromAnnotationSubject(annotation)
  if (subject === null) {
    /**
     * If the subject is "UnprocessedSubject", which should never happen (for FE)
     * "getCacheInputFromAnnotationSubject" will return null, because FE can't represent this
     * no need to proceed with the cache operations
     */
    return
  }

  const variables = {
    shareIdentifier,
    versionIdentifier,
    subject,
  }

  const addNewAnnotationQuery = ({
    annotations,
  }: WritableDraft<GetAnnotationsQuery>) => {
    const doesAnnotationAlreadyExist = annotations.entries.find(
      ({ identifier }) => annotation.identifier === identifier
    )

    if (!doesAnnotationAlreadyExist) {
      annotations.entries.unshift(annotation)
      annotations.meta.totalCount++
    }
  }

  // [1] add the annotation to its annotations listing
  // To the active listing
  updateAnnotationsInboxOrder(
    apollo,
    { ...variables, annotationStatus: 'ACTIVE_ONLY', sort: 'NEW_FIRST' },
    annotation
  )

  // To the all listing
  updateMultipleAnnotationQueries(
    apollo,
    { ...variables, annotationStatus: 'ALL', sort: 'LATEST_FIRST' },
    addNewAnnotationQuery
  )

  // [2] add to the annotation dots query
  // To the active listing
  updateMultipleAnnotationDotQueries(
    apollo,
    { ...variables, annotationStatus: 'ACTIVE_ONLY' },
    addNewAnnotationQuery
  )

  // To the all listing
  updateMultipleAnnotationDotQueries(
    apollo,
    { ...variables, annotationStatus: 'ALL' },
    addNewAnnotationQuery
  )
}

export const moveAnnotationInCache = (
  apollo: DataProxy,
  { shareIdentifier, versionIdentifier }: AdditionalProps,
  annotation: BaseAnnotationFragment
) => {
  /**
   * In order to successfully move an annotation
   * we need to:
   * [programmatically]
   * - check if the subject has changed and remove/add to its query [1]
   * - update the older annotation counter [2]
   *
   * [fragment BaseAnnotation refetch]
   * - update new subject annotation counters
   */

  /**
   * [1] check if the subject has changed and remove/add to its query
   *
   * We are fetching the current annotation from cache (before applying the subscription data)
   * to double-check if the subject (artboard/page) have changed, if it has we need to update the
   * queries
   */

  const cachedAnnotation = getAnnotationSubject(apollo, annotation.identifier)
  const subject = getCacheInputFromAnnotationSubject(annotation)

  if (subject === null) {
    /**
     * If the subject is "UnprocessedSubject", which should never happen (for FE)
     * "getCacheInputFromAnnotationSubject" will return null, because FE can't represent this
     * no need to proceed with the cache operations
     */
    return
  }

  if (
    cachedAnnotation &&
    isSameAnnotationSubject(annotation, cachedAnnotation)
  ) {
    /**
     * If the annotation.currentArtboard is the same this
     * means the annotation only was moved inside the artboard
     * so we don't need to add additional logic.
     *
     * If it's a move in a page annotation this will still be null and
     * the logic still applies
     */
    return
  }

  if (annotation.currentSubject.__typename === 'Artboard') {
    // Add to the Active list
    updateAnnotationsInboxOrder(
      apollo,
      {
        shareIdentifier,
        versionIdentifier,
        subject,
        annotationStatus: 'ACTIVE_ONLY',
        sort: 'NEW_FIRST',
      },
      annotation
    )

    // Add to the All list
    updateAnnotationsAllOrder(
      apollo,
      {
        shareIdentifier,
        versionIdentifier,
        subject,
        annotationStatus: 'ALL',
        sort: 'LATEST_FIRST',
      },
      annotation
    )

    {
      /**
       * This subject is isolated because these queries are only regarding artboard
       * so we should remove the permanentPageId from the subject
       */
      const subject = {
        type: 'ARTBOARD' as const,
        permanentId: annotation.currentSubject.uuid,
      }

      const addAnnotation = ({
        annotations,
      }: WritableDraft<GetAnnotationsQuery>) => {
        annotations.entries.unshift(annotation)
        annotations.meta.totalCount++
      }

      // Add to the Active dot list
      updateAnnotationsDotsQuery(
        apollo,
        {
          shareIdentifier,
          versionIdentifier,
          subject,
          annotationStatus: 'ACTIVE_ONLY',
        },
        addAnnotation
      )

      // Add to the All dot list
      updateAnnotationsDotsQuery(
        apollo,
        {
          shareIdentifier,
          versionIdentifier,
          subject,
          annotationStatus: 'ALL',
        },
        addAnnotation
      )
    }
  }

  if (cachedAnnotation?.currentSubject?.__typename === 'Artboard') {
    /**
     * Create the subject input to pass to the
     * apollo cache updaters
     */
    const subject = {
      type: 'ARTBOARD' as const,
      permanentId: cachedAnnotation.currentSubject.uuid,
    }

    const deleteAnnotation = ({
      annotations,
    }: WritableDraft<GetAnnotationsQuery>) => {
      annotations.entries = annotations.entries.filter(
        ({ identifier }) => identifier !== annotation.identifier
      )
      annotations.meta.totalCount--
    }

    // Remove from the Active list
    updateAnnotationsQuery(
      apollo,
      {
        shareIdentifier,
        versionIdentifier,
        subject,
        annotationStatus: 'ACTIVE_ONLY',
        sort: 'NEW_FIRST',
      },
      deleteAnnotation
    )

    // Remove from to the All list
    updateAnnotationsQuery(
      apollo,
      {
        shareIdentifier,
        versionIdentifier,
        subject,
        annotationStatus: 'ALL',
        sort: 'LATEST_FIRST',
      },
      deleteAnnotation
    )

    // Remove from to the Active dot list
    updateAnnotationsDotsQuery(
      apollo,
      {
        shareIdentifier,
        versionIdentifier,
        subject,
        annotationStatus: 'ACTIVE_ONLY',
      },
      deleteAnnotation
    )

    // Remove from to the All dot list
    updateAnnotationsDotsQuery(
      apollo,
      {
        shareIdentifier,
        versionIdentifier,
        subject,
        annotationStatus: 'ALL',
      },
      deleteAnnotation
    )

    /**
     * [2] update the older annotation counter
     */
    const {
      documentVersionShortId,
      permanentArtboardShortId,
    } = cachedAnnotation.currentSubject

    const artboardId = dataIdFromObject({
      __typename: 'Artboard',
      documentVersionShortId,
      permanentArtboardShortId,
    })!

    const artboardCounters = errorPreventiveFragmentRead<AnnotationArtboardInfoFragment>(
      apollo,
      {
        fragment: AnnotationArtboardInfoFragmentDoc,
        fragmentName: 'AnnotationArtboardInfo',
        id: artboardId,
      }
    )

    artboardCounters &&
      apollo.writeFragment<AnnotationArtboardInfoFragment>({
        fragment: AnnotationArtboardInfoFragmentDoc,
        fragmentName: 'AnnotationArtboardInfo',
        id: artboardId,
        data: {
          ...artboardCounters,
          annotationCount: Math.max(artboardCounters.annotationCount - 1, 0),
          unreadCount: Math.max(artboardCounters.unreadCount - 1, 0),
        },
      })
  }
}

export const resolveAnnotation = (
  apollo: DataProxy,
  { shareIdentifier, versionIdentifier }: AdditionalProps,
  annotation: AnnotationSubjectFragment
) => {
  /**
   * In order to successfully resolve an annotation
   * we need to:
   * [programmatically]
   * - remove the annotation from all the active queries [1]
   * - add the annotation to the resolved queries [2]
   *
   * [fragment BaseAnnotation refetch]
   * - Update the subject annotation counters
   * - Update the resolution status
   */

  const { identifier } = annotation

  // [1] Remove the annotation from all the active queries
  removeFromPaginated(apollo, { __typename: 'Annotation', identifier }, key =>
    key.includes('ACTIVE_ONLY')
  )

  // [2] Add the annotation to the resolved queries
  const subject = getCacheInputFromAnnotationSubject(annotation)

  if (subject === null) {
    /**
     * If the subject is "UnprocessedSubject" which never shouldn't (for FE)
     * "getCacheInputFromAnnotationSubject" will return null, because FE can't represent this
     * no need to proceed with the cache operations
     */
    return
  }

  const variables = {
    shareIdentifier,
    versionIdentifier,
    subject,
    annotationStatus: 'RESOLVED_ONLY' as const,
  }

  /**
   * To prevent including the "BaseAnnotation" fragment on the
   * "resolveAnnotation" mutation, it would force a huge optimistic response,
   * it was opted to get the base annotation fragment isolated.
   *
   * Since this fragment will only be relevant when the resolved sidebar is visible,
   * this doesn't occur when you are resolving the annotation on the active sidebar (filter)
   *
   * If the user is resolving a annotation (in the popover) with the resolved sidebar visible on the side
   * the "annotationComments" query already includes the "BaseAnnotation" fragment
   * to make sure the sidebar is updated has it included
   */
  const baseAnnotation = getBaseAnnotation(apollo, annotation.identifier)
  if (baseAnnotation) {
    updateMultipleAnnotationQueries(
      apollo,
      { ...variables, sort: 'LATEST_RESOLVED_FIRST' },
      ({ annotations }) => {
        annotations.entries.unshift(baseAnnotation)
        annotations.meta.totalCount++
      }
    )

    updateMultipleAnnotationDotQueries(apollo, variables, ({ annotations }) => {
      annotations.entries.push(baseAnnotation)
      annotations.meta.totalCount++
    })
  }
}

export const unResolveAnnotation = (
  apollo: DataProxy,
  { shareIdentifier, versionIdentifier }: AdditionalProps,
  annotation: AnnotationSubjectFragment
) => {
  /**
   * In order to successfully un-resolve an annotation
   * we need to:
   * [programmatically]
   * - remove the annotation from all the resolved queries [1]
   * - add the annotation to the active queries [2]
   *
   * [fragment BaseAnnotation refetch]
   * - Update the subject annotation counters
   * - Update the resolution status
   */
  const { identifier } = annotation

  // [1] Remove the annotation from all the resolved queries
  removeFromPaginated(apollo, { __typename: 'Annotation', identifier }, key =>
    key.includes('RESOLVED_ONLY')
  )

  // [2] Add the annotation to the active queries
  const subject = getCacheInputFromAnnotationSubject(annotation)

  if (subject === null) {
    /**
     * If the subject is "UnprocessedSubject" which never shouldn't (for FE)
     * "getCacheInputFromAnnotationSubject" will return null, because FE can't represent this
     * no need to proceed with the cache operations
     */
    return
  }

  const variables = {
    shareIdentifier,
    versionIdentifier,
    subject,
    annotationStatus: 'ACTIVE_ONLY' as const,
  }

  /**
   * To prevent including the "BaseAnnotation" fragment on the
   * "unresolveAnnotation" mutation, it would force a huge optimistic response,
   * it was opted to get the base annotation fragment isolated.
   *
   * Since this fragment will only be relevant when the active sidebar is visible,
   * this doesn't occur when you are un-resolving the annotation on the resolved sidebar (filter)
   *
   * If the user is un-resolving a annotation (in the popover) with the active sidebar visible on the side
   * the "annotationComments" query already includes the "BaseAnnotation" fragment
   * to make sure the sidebar is updated has it included
   */
  const baseAnnotation = getBaseAnnotation(apollo, annotation.identifier)
  if (baseAnnotation) {
    updateAnnotationsInboxOrder(
      apollo,
      { ...variables, sort: 'NEW_FIRST' },
      baseAnnotation
    )

    updateMultipleAnnotationDotQueries(apollo, variables, ({ annotations }) => {
      annotations.entries.push(baseAnnotation)
      annotations.meta.totalCount++
    })
  }
}
